import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { useUi } from "../../ui";
import Field from "../../vip/components/Field";
import {
  DEFAULT_SHIFT,
  SHIFT_GRACE_PERIOD_OPTIONS,
  SHIFT_TIME_OPTIONS,
  SLOT_MINUTES_OPTIONS,
} from "../constants";
import { formatTimeOfDay, validateShift } from "../shiftHelpers";

const useStyles = makeStyles({
  row: {
    padding: 20,
    borderTop: `1px solid ${T.borderSoft}`,
    "&:first-child": { borderTop: "none" },
  },
  inactive: { opacity: 0.55 },
  summary: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  window: { fontSize: 14, fontWeight: 600, color: T.fg },
  meta: { fontSize: 12, color: T.mutedFg },
  chip: {
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    padding: "2px 8px",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: T.mutedFg,
  },
  fields: {
    display: "grid",
    gridTemplateColumns: "1.2fr repeat(6, 1fr)",
    gap: 12,
    "@media (max-width: 1100px)": { gridTemplateColumns: "1fr 1fr 1fr" },
    "@media (max-width: 700px)": { gridTemplateColumns: "1fr" },
  },
  description: { marginTop: 12 },
  toggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    fontSize: 13,
    color: T.fg,
  },
  error: { marginTop: 8, fontSize: 12, color: T.danger },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 12 },
  secondaryButton: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    color: T.mutedFg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": { background: T.surface },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  deleteButton: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    color: T.mutedFg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": { borderColor: T.danger, color: T.danger },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  confirmButton: {
    borderRadius: 6,
    border: `1px solid ${T.danger}`,
    background: T.danger,
    color: "#fff",
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  confirmPrompt: { marginRight: "auto", alignSelf: "center", fontSize: 13, color: T.danger },
});

function toEditableShift(shift) {
  return {
    id: shift.id,
    name: shift.name || "",
    description: shift.description || "",
    startTime: shift.startTime ?? DEFAULT_SHIFT.startTime,
    endTime: shift.endTime ?? DEFAULT_SHIFT.endTime,
    gracePeriodMinutes: shift.gracePeriodMinutes ?? DEFAULT_SHIFT.gracePeriodMinutes,
    maxCollectPerSlot: shift.maxCollectPerSlot ?? DEFAULT_SHIFT.maxCollectPerSlot,
    maxParkPerSlot: shift.maxParkPerSlot ?? DEFAULT_SHIFT.maxParkPerSlot,
    slotMinutes: shift.slotMinutes ?? DEFAULT_SHIFT.slotMinutes,
    isActive: shift.isActive ?? true,
  };
}

export default function ShiftRow({ shift, disabled, onDelete, onSave }) {
  const s = useStyles();
  const ui = useUi();
  const original = useMemo(() => toEditableShift(shift), [shift]);
  const [draft, setDraft] = useState(original);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setDraft(original);
  }, [original]);

  const setField = (field) => (event) => {
    const { value } = event.target;
    setDraft((current) => ({ ...current, [field]: value }));
  };

  /* Select values arrive as strings; the API takes Int. */
  const setMinutesField = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    setDraft((current) => ({ ...current, [field]: value }));
  };

  /* An emptied box stays empty rather than becoming NaN; validateShift rejects it. */
  const setCountField = (field) => (event) => {
    const { value } = event.target;
    setDraft((current) => ({ ...current, [field]: value === "" ? "" : Number(value) }));
  };

  const toggleActive = (event) => {
    const { checked } = event.target;
    setDraft((current) => ({ ...current, isActive: checked }));
  };

  const overnight = draft.endTime <= draft.startTime;
  const errors = validateShift(draft);
  const firstError = Object.values(errors)[0];
  const isDirty = JSON.stringify(draft) !== JSON.stringify(original);
  const isBlocked = disabled || submitting;
  const canSave = !firstError && isDirty && !isBlocked;

  const save = async (event) => {
    event.preventDefault();
    if (!canSave) return;

    setSubmitting(true);
    try {
      await onSave(draft);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (isBlocked) return;

    setSubmitting(true);
    try {
      await onDelete(shift);
    } finally {
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <li className={original.isActive ? s.row : `${s.row} ${s.inactive}`}>
      <form onSubmit={save}>
        <div className={s.summary}>
          <span className={s.window}>
            {formatTimeOfDay(draft.startTime)} &ndash; {formatTimeOfDay(draft.endTime)}
          </span>
          {overnight && <span className={s.chip}>Overnight</span>}
          <span className={s.meta}>
            park {draft.maxParkPerSlot} &middot; collect {draft.maxCollectPerSlot} per{" "}
            {draft.slotMinutes} min
          </span>
        </div>

        <div className={s.fields}>
          <Field label="Name">
            <input className={ui.field} disabled={isBlocked} value={draft.name} onChange={setField("name")} />
          </Field>
          <Field label="Start time">
            <select className={ui.field} disabled={isBlocked} value={draft.startTime} onChange={setMinutesField("startTime")}>
              {SHIFT_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>
          <Field label="End time">
            <select className={ui.field} disabled={isBlocked} value={draft.endTime} onChange={setMinutesField("endTime")}>
              {SHIFT_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Grace period">
            <select className={ui.field} disabled={isBlocked} value={draft.gracePeriodMinutes} onChange={setMinutesField("gracePeriodMinutes")}>
              {SHIFT_GRACE_PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Max pickups per period">
            <input
              className={ui.field}
              type="number"
              min="1"
              disabled={isBlocked}
              value={draft.maxCollectPerSlot}
              onChange={setCountField("maxCollectPerSlot")}
            />
          </Field>
          <Field label="Max to park per period">
            <input
              className={ui.field}
              type="number"
              min="1"
              disabled={isBlocked}
              value={draft.maxParkPerSlot}
              onChange={setCountField("maxParkPerSlot")}
            />
          </Field>
          <Field label="Period length">
            <select className={ui.field} disabled={isBlocked} value={draft.slotMinutes} onChange={setMinutesField("slotMinutes")}>
              {SLOT_MINUTES_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Description" className={s.description}>
          <input className={ui.field} disabled={isBlocked} value={draft.description} onChange={setField("description")} />
        </Field>

        <label className={s.toggle}>
          <input type="checkbox" disabled={isBlocked} checked={draft.isActive} onChange={toggleActive} />
          Active
        </label>

        {isDirty && firstError && <div className={s.error}>{firstError}</div>}

        <div className={s.actions}>
          {confirmingDelete ? (
            <>
              <span className={s.confirmPrompt}>Delete this shift?</span>
              <button type="button" className={s.secondaryButton} disabled={isBlocked} onClick={() => setConfirmingDelete(false)}>
                Keep
              </button>
              <button type="button" className={s.confirmButton} disabled={isBlocked} onClick={remove}>
                Delete
              </button>
            </>
          ) : (
            <>
              <button type="button" className={s.deleteButton} disabled={isBlocked} onClick={() => setConfirmingDelete(true)}>
                Delete
              </button>
              <button type="button" className={s.secondaryButton} disabled={!isDirty || isBlocked} onClick={() => setDraft(original)}>
                Cancel
              </button>
              <button type="submit" className={ui.primaryBtn} disabled={!canSave}>
                Save
              </button>
            </>
          )}
        </div>
      </form>
    </li>
  );
}
